import * as XLSX from 'xlsx';
import type { UserActivityResponse } from '@services/adminActivity';

function sanitizeFilenamePart(value: string): string {
	return value
		.toLowerCase()
		.replace(/[^a-z0-9]+/g, '-')
		.replace(/^-+|-+$/g, '')
		.slice(0, 40);
}

function daysBetween(from: Date, to: Date): number {
	const ms = to.getTime() - from.getTime();
	return Math.max(0, Math.floor(ms / (1000 * 60 * 60 * 24)));
}

function pct(numerator: number, denominator: number): string {
	if (denominator <= 0) return '—';
	return `${Math.round((numerator / denominator) * 100)}%`;
}

function minutesFromSeconds(seconds: number | null | undefined): number | null {
	if (seconds == null || seconds <= 0) return null;
	return Math.round((seconds / 60) * 10) / 10;
}

function avgPlanGenerationSeconds(plans: UserActivityResponse['plans_list']): number | null {
	const measured = plans
		.map((p) => p.generation_duration_seconds)
		.filter((s): s is number => s != null && s >= 0);
	if (measured.length === 0) return null;
	const total = measured.reduce((sum, val) => sum + val, 0);
	return Math.round(total / measured.length);
}

function windowLabel(days: number): string {
	if (days === 0) return 'All time';
	if (days === 7) return 'Last 7 days';
	if (days === 30) return 'Last 30 days';
	if (days === 90) return 'Last 90 days';
	if (days === 365) return 'Last year';
	return `Last ${days} days`;
}

function primaryLabel<T extends { events: number }>(
	rows: T[],
	key: keyof T
): string {
	if (!rows.length) return '—';
	const top = [...rows].sort((a, b) => b.events - a.events)[0];
	return String(top[key] ?? '—');
}

export function downloadUserActivityExcel(
	report: UserActivityResponse,
	windowDays: number
): void {
	const now = new Date();
	const exportedAt = now.toISOString();
	const { user, activity, plans, projects, offers } = report;

	const accountCreated = user.created_at ? new Date(user.created_at) : null;
	const lastSeen = activity.last_seen_at ? new Date(activity.last_seen_at) : null;

	const accountAgeDays = accountCreated ? daysBetween(accountCreated, now) : null;
	const daysSinceLastSeen = lastSeen ? daysBetween(lastSeen, now) : null;
	const avgSessionMinutes =
		activity.total_sessions > 0
			? minutesFromSeconds(activity.engaged_seconds / activity.total_sessions)
			: null;
	const avgPlanGenSeconds = avgPlanGenerationSeconds(report.plans_list);
	const questionnaireRate = pct(activity.questionnaire_submits, activity.questionnaire_starts);
	const generationRate = pct(activity.plan_generation_successes, activity.plan_generation_starts);

	const summaryRows: (string | number | null)[][] = [
		['HortEra — User activity report'],
		['Exported at', exportedAt],
		['Activity window', report.window_days == null ? 'All time' : windowLabel(windowDays)],
		[],
		['User'],
		['Name', user.name],
		['Email', user.email],
		['Role', user.role],
		['Company', user.company_name ?? '—'],
		['Account created', user.created_at ?? '—'],
		['Account age (days)', accountAgeDays ?? '—'],
		['Profile last updated', user.updated_at ?? '—'],
		[],
		['Engagement (logged-in activity in window)'],
		['First tracked', activity.first_seen_at ?? '—'],
		['Last seen', activity.last_seen_at ?? '—'],
		['Days since last seen', daysSinceLastSeen ?? '—'],
		['Active days', activity.active_days],
		['Sessions', activity.total_sessions],
		['Total events', activity.total_events],
		['Time in app (minutes)', minutesFromSeconds(activity.engaged_seconds) ?? 0],
		['Avg minutes per session', avgSessionMinutes ?? '—'],
		['Avg events per session', activity.total_sessions > 0 ? Math.round(activity.total_events / activity.total_sessions) : '—'],
		['Logins', activity.login_successes],
		['Primary device', primaryLabel(report.devices, 'device_type')],
		['Primary browser', primaryLabel(report.browsers, 'browser')],
		[],
		['Questionnaire & AI plans'],
		['Questionnaire starts', activity.questionnaire_starts],
		['Questionnaire submits', activity.questionnaire_submits],
		['Questionnaire completion rate', questionnaireRate],
		['Validation errors', activity.questionnaire_validation_errors],
		['Upload errors', activity.questionnaire_upload_errors],
		['Plan generation starts', activity.plan_generation_starts],
		['Plan generation successes', activity.plan_generation_successes],
		['Plan generation errors', activity.plan_generation_errors],
		['Plan generation success rate', generationRate],
		['Avg plan generation time (seconds)', avgPlanGenSeconds ?? '—'],
		[],
		['Plans (database, all time)'],
		['Total plans', plans.total],
		['Draft', plans.draft],
		['Published', plans.published],
		['Closed', plans.closed],
		['Archived', plans.archived],
		['Hidden (moderation)', plans.hidden],
		['Deleted (moderation)', plans.deleted],
		[],
		['Projects (from user plans)'],
		['Total', projects.total],
		['Planning', projects.planning],
		['Quoted', projects.quoted],
		['In progress', projects.in_progress],
		['Completed', projects.completed],
		['Cancelled', projects.cancelled],
		[],
		['Offers'],
		['Received on user plans (total)', offers.received_on_plans],
		['Received — accepted', offers.received_by_status?.accepted ?? 0],
		['Received — pending', offers.received_by_status?.pending ?? 0],
		['Received — rejected', offers.received_by_status?.rejected ?? 0],
		['Sent as gardener (total)', offers.sent_as_gardener],
		['Sent — accepted', offers.sent_by_status?.accepted ?? 0],
		['Sent — pending', offers.sent_by_status?.pending ?? 0],
		['Sent — rejected', offers.sent_by_status?.rejected ?? 0],
		[],
		['Notes'],
		[
			'Time in app uses session heartbeats while the tab is visible. Anonymous use before login is not attributed to this user.'
		]
	];

	const dailyRows = [
		['Day', 'Time in app (min)', 'Sessions', 'Events'],
		...report.daily_activity.map((row) => [
			row.day,
			minutesFromSeconds(Number(row.engaged_seconds)) ?? 0,
			row.sessions,
			row.events
		])
	];

	const plansRows = [
		[
			'Plan ID',
			'Title',
			'Status',
			'Moderation',
			'Generation time (sec)',
			'Created',
			'Updated',
			'Published'
		],
		...report.plans_list.map((plan) => [
			plan.id,
			plan.title,
			plan.status,
			plan.moderation_status ?? 'visible',
			plan.generation_duration_seconds ?? '',
			plan.created_at,
			plan.updated_at,
			plan.published_at ?? ''
		])
	];

	const eventsRows = [
		['Event', 'Count'],
		...report.top_events.map((row) => [row.event_name, row.events])
	];

	const recentRows = [
		['When', 'Event', 'Page', 'Duration (sec)', 'Device', 'Browser'],
		...report.recent_events.map((event) => [
			event.created_at,
			event.event_name,
			event.page_path ?? '',
			event.duration_seconds ?? '',
			event.device_type ?? '',
			event.browser ?? ''
		])
	];

	const deviceRows = [
		['Device', 'Events'],
		...report.devices.map((row) => [row.device_type, row.events])
	];

	const browserRows = [
		['Browser', 'Events'],
		...report.browsers.map((row) => [row.browser, row.events])
	];

	const wb = XLSX.utils.book_new();
	XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(summaryRows), 'Summary');
	XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(dailyRows), 'Daily activity');
	XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(plansRows), 'Plans');
	XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(eventsRows), 'Top events');
	XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(recentRows), 'Recent events');
	XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(deviceRows), 'Devices');
	XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(browserRows), 'Browsers');

	const datePart = now.toISOString().slice(0, 10);
	const namePart = sanitizeFilenamePart(user.name || user.email || `user-${user.id}`);
	const filename = `horteira-user-activity-${namePart}-${datePart}.xlsx`;

	XLSX.writeFile(wb, filename);
}
